/* Token values that JavaScript needs to reason about — picking an avatar colour,
   mapping a backend status string to a pill tone, colouring chart segments.
   Anything purely presentational belongs in tokens.css instead. */

export const AVATAR_PALETTE = [
  '#8E1B3F',
  '#D62246',
  '#E8543F',
  '#F5A742',
  '#7A5C3E',
  '#2C5AA0',
];

export const CHART = {
  flow: '#F5A742',
  credit: '#8E1B3F',
  positive: '#1F7A5C',
  negative: '#C41E45',
};

/* Pill tones. `tone` is the vocabulary components speak; backend status strings are
   translated into a tone by statusTone() below. */
export const TONES = {
  live: { bg: 'var(--status-live-bg)', color: 'var(--status-live-text)' },
  review: { bg: 'var(--status-review-bg)', color: 'var(--status-review-text)' },
  blocked: { bg: 'var(--status-blocked-bg)', color: 'var(--status-blocked-text)' },
  neutral: { bg: 'var(--status-neutral-bg)', color: 'var(--status-neutral-text)' },
  info: { bg: 'var(--status-info-bg)', color: 'var(--status-info-text)' },
};

/* Maps the status vocabularies the API actually returns onto pill tones.
   Keys are lower-cased and underscores/spaces normalised before lookup. */
const STATUS_TONES = {
  // partner / agent lifecycle
  live: 'live',
  approved: 'live',
  active: 'live',
  success: 'live',
  completed: 'live',
  delivered: 'live',
  dispatched: 'live',
  published: 'live',
  paid: 'live',

  under_review: 'review',
  pending: 'review',
  review_pending: 'review',
  processing: 'review',
  ordered: 'review',
  upcoming: 'review',
  scheduled: 'review',
  feedback_requested: 'review',
  payment_pending: 'review',
  draft: 'review',

  on_hold: 'neutral',
  setup: 'neutral',
  closed: 'neutral',
  expired: 'neutral',
  exhausted: 'neutral',
  inactive: 'neutral',
  unpublished: 'neutral',

  blacklisted: 'blocked',
  rejected: 'blocked',
  cancelled: 'blocked',
  refunded: 'blocked',
  refund_initiated: 'blocked',
  failed: 'blocked',
  payment_failed: 'blocked',
  not_conducted: 'blocked',
  off_boarded: 'blocked',

  private: 'info',
  open: 'info',
};

export function normaliseStatus(status) {
  if (!status) return '';
  return String(status).trim().toLowerCase().replace(/[\s-]+/g, '_');
}

export function statusTone(status) {
  return STATUS_TONES[normaliseStatus(status)] || 'neutral';
}

/* "under_review" -> "Under Review". Used wherever a raw status reaches the UI. */
export function statusLabel(status) {
  if (!status) return '—';
  return String(status)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/* Deterministic avatar colour: the same person keeps the same colour across screens. */
export function avatarColor(seed) {
  const key = String(seed || '');
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

export function initialsOf(name) {
  if (!name) return '?';
  return String(name)
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
